import { Project } from '@prisma/client';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { Alert, AlertDescription, AlertTitle, Input, Label } from '@zond/ui';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ConfirmDialog } from '../../../../components/confirm-dialog';
import { useProjects } from '../context';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentRow: Project;
  onDelete: (projectId: number) => Promise<void>;
  isDeleting?: boolean;
}

export function ProjectDeleteDialog({ open, onOpenChange, currentRow, onDelete, isDeleting }: Props) {
  const { t } = useTranslation('projects');
  const [value, setValue] = useState('');
  const { setOpen } = useProjects();

  const handleDelete = async () => {
    if (value.trim() !== currentRow.name) return;

    try {
      await onDelete(currentRow.id);
      setValue('');
      setOpen(null);
    } catch (error) {
      // Error will be handled in the parent component
      console.error('Failed to delete project:', error);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setValue('');
    }
    onOpenChange(isOpen);
  };

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={handleOpenChange}
      handleConfirm={handleDelete}
      disabled={value.trim() !== currentRow.name || isDeleting}
      confirmText={isDeleting ? t('common:states.deleting') : t('common:actions.delete')}
      title={
        <span className="text-destructive">
          <ExclamationTriangleIcon className="mr-1 inline-block stroke-destructive" width={18} height={18} />{' '}
          {t('delete.title')}
        </span>
      }
    >
      <Alert variant="destructive">
        <ExclamationTriangleIcon className="h-4 w-4" />
        <AlertTitle>{t('delete.warningTitle')}</AlertTitle>
        <AlertDescription>{t('delete.warningDescription')}</AlertDescription>
      </Alert>
      <div className="mt-4">
        <Label htmlFor="confirmDelete">
          <Trans
            i18nKey="delete.confirmLabel"
            t={t}
            values={{ name: currentRow.name }}
            components={{ bold: <span className="font-semibold" /> }}
          />
        </Label>
        <Input
          id="confirmDelete"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="mt-2"
          placeholder={t('delete.inputPlaceholder')}
        />
      </div>
    </ConfirmDialog>
  );
}
